import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const LandingPage = () => {
  const nav = useNavigate();
  const [modal, setModal] = useState(null);
  const [chatbotModal, setChatbotModal] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const verticals = [
    {
      badge: '🤖 AI Chatbot Suite',
      color: '#06b6d4',
      trigger: 'Any client — embeddable widget',
      features: [
        { icon: '🧑‍💼', title: 'Live Human Takeover', desc: 'Agent can take over any conversation in real-time. Bot goes idle, human handles — then hands back to bot seamlessly' },
        { icon: '🚫', title: 'Content Filter', desc: 'Auto-detects and blocks profanity, adult content & abusive language. Configurable word list per client' },
        { icon: '📋', title: 'Auto Lead Generation', desc: 'Bot collects name, email & phone naturally in conversation. Leads saved to dashboard — zero manual effort' },
        { icon: '📚', title: 'Domain Knowledge Base', desc: '150+ topic JSON knowledge base with smart keyword search, contextual suggestions & conversational AI patterns' },
      ],
    },
    {
      badge: '👥 HR Suite',
      color: '#6366f1',
      trigger: 'RESUME feature',
      features: [
        { icon: '📄', title: 'Resume Parsing', desc: 'AI-powered resume parsing with structured JSON output and field extraction' },
        { icon: '🎯', title: 'ATS Scoring Engine', desc: 'Trainable per client — score resumes 0–100 against custom job profiles with skill/experience/education weights' },
        { icon: '📝', title: 'Candidate Test Generator', desc: 'Create 25/50-question tests, share links, auto-score with built-in proctoring & screenshot capture' },
        { icon: '🔄', title: 'Candidate Pipeline', desc: 'Applied → Shortlisted → Test → Interview → Selected. Notes, bulk export, stage management' },
      ],
    },
    {
      badge: '⚖️ Legal & CA Suite',
      color: '#a855f7',
      trigger: 'NOTICE / SUMMARY / OTHEREVENTS features',
      features: [
        { icon: '📋', title: 'Notice Tracker', desc: 'Auto-extract notice type, section, AY, due date, amount. Status tracking with due-date alerts' },
        { icon: '📚', title: 'Summary Library', desc: 'Full-text searchable AI summaries — filter, export PDF, compare side-by-side, bookmark' },
        { icon: '📅', title: 'Compliance Calendar', desc: 'Auto-populate deadlines from notices. Color-coded urgency, Google Calendar / iCal export' },
        { icon: '✨', title: 'AI Summarization', desc: 'Intelligent document summarization with key points & full text analysis' },
      ],
    },
    {
      badge: '🏦 NBFC / Financial Suite',
      color: '#ec4899',
      trigger: 'BANK_STATEMENT / Govt ID features',
      features: [
        { icon: '📊', title: 'Bank Statement Analyzer', desc: 'Income analysis, expense categorization, bounce detection, AMB, debt ratio & fraud flags' },
        { icon: '🪪', title: 'KYC Dashboard', desc: 'Link PAN + Aadhaar + DL + Voter ID per applicant. Cross-verify names, flag mismatches, track expiry' },
        { icon: '💰', title: 'Loan Eligibility Engine', desc: 'Client-configurable rules — multiplier, min AMB, max bounces. Auto Eligible / Conditional / Rejected' },
        { icon: '⚠️', title: 'Risk Reports', desc: 'Risk score 0–100 based on bounces, overdraft, irregular income, unusual transaction patterns' },
      ],
    },
  ];

  const chatbotFeatures = [
    { icon: '🧑‍💼', title: 'Live Human Takeover', desc: 'Agent jumps into any chat in real-time. Bot pauses, human resolves — bot resumes after handoff', color: '#06b6d4' },
    { icon: '🚫', title: 'Profanity & Content Filter', desc: 'Auto-blocks abusive, adult & offensive language. Custom word list per client. Keeps conversations clean', color: '#ef4444' },
    { icon: '📋', title: 'Auto Lead Generation', desc: 'Collects name, email & phone mid-conversation. All leads saved to admin dashboard automatically', color: '#22c55e' },
    { icon: '📚', title: 'Domain Knowledge Base', desc: '150+ topic knowledge base with smart keyword search, NLP patterns & contextual suggestions', color: '#6366f1' },
    { icon: '🎤', title: 'Voice I/O', desc: 'Speech-to-text input + AI voice replies. Toggle per session, works on all modern browsers', color: '#0ea5e9' },
    { icon: '🔌', title: 'JS Widget Embed', desc: 'Single script tag on any website. Offline fallback — bot answers from static JSON when server is down', color: '#8b5cf6' },
    { icon: '📊', title: 'Analytics Dashboard', desc: 'Conversation logs, lead reports, query analytics & response performance per client', color: '#f59e0b' },
    // { icon: '🏢', title: '10+ Live Clients', desc: 'Vision IAS, Drishti, PW, Unacademy, Vajiram & more — 10,000+ daily queries', color: '#ec4899' },
  ];

  const chatbotROI = [
    { icon: '💰', title: 'Cut Support Costs by 60–80%', desc: 'Bot handles repetitive queries 24/7 — no salary, no breaks, no sick days. One bot replaces 3–5 support agents for routine questions.' },
    { icon: '📋', title: 'Auto Lead Capture — Zero Effort', desc: 'Every visitor who chats becomes a lead. Bot collects name, email & phone naturally. No forms, no friction — leads flow directly to your CRM or dashboard.' },
    { icon: '⚡', title: 'Instant Response — 0 Wait Time', desc: 'Customers get answers in milliseconds, not minutes. Studies show 82% of customers expect instant replies — bot delivers every time.' },
    { icon: '🕐', title: '24/7 Availability', desc: 'Your business never sleeps. Bot handles queries at 2 AM on Sunday just as well as 10 AM Monday. No overtime, no missed leads.' },
    { icon: '🧑‍💼', title: 'Human Takeover When It Matters', desc: 'Complex or sensitive queries escalate to a live agent instantly. Bot handles volume, humans handle value — best of both worlds.' },
    { icon: '🚫', title: 'Brand-Safe Conversations', desc: 'Built-in profanity & content filter keeps every conversation professional. No embarrassing bot replies — your brand stays protected.' },
    { icon: '📈', title: 'Scale Without Hiring', desc: 'Handle 10x more queries during peak season without adding headcount. Bot scales instantly — no training, no onboarding.' },
    { icon: '🎯', title: 'Higher Conversion Rate', desc: 'Engaged visitors convert 3x more than passive ones. Bot proactively answers objections, collects intent signals & pushes users toward action.' },
    { icon: '🔌', title: 'Deploy in 1 Day', desc: 'One script tag on your website. No dev team needed. Custom brand, colors & knowledge base configured by us — live within 24 hours.' },
    { icon: '📊', title: 'Full Visibility', desc: 'See every conversation, every lead, every unanswered question. Analytics dashboard shows what users ask most — improve your product with real data.' },
  ];

  const features = [
    { icon: '🪪', title: 'PAN Card OCR', desc: 'Extract PAN number, name, DOB with 98% accuracy using dual-engine AI', color: '#6366f1' },
    { icon: '🆔', title: 'Aadhaar Card', desc: 'Front + back processing with 12-digit validation and address extraction', color: '#8b5cf6' },
    { icon: '🚗', title: 'Driving License', desc: 'All Indian state formats supported with vehicle category detection', color: '#a855f7' },
    { icon: '🗳️', title: 'Voter ID', desc: 'Electoral data extraction — name, address, EPIC number parsing', color: '#d946ef' },
    { icon: '📄', title: 'Resume / ATS', desc: 'AI-powered resume parsing with trainable ATS scoring 0–100 per job profile', color: '#ec4899' },
    { icon: '📋', title: 'Notice AI', desc: 'Tax notices & legal documents — smart field extraction, status tracking & due-date alerts', color: '#f43f5e' },
    { icon: '✨', title: 'AI Summary', desc: 'Intelligent document summarization with key points & full text analysis', color: '#f97316' },
    { icon: '🏦', title: 'Bank Statement', desc: 'Deep financial analysis — income, expenses, bounce detection, risk score & fraud flags', color: '#0ea5e9' },
  ];

  const howItWorks = [
    { step: '01', title: 'Authenticate', desc: 'Get JWT token via secure multi-tenant API', icon: '🔐' },
    { step: '02', title: 'Upload', desc: 'Send document via REST API or client portal', icon: '📤' },
    { step: '03', title: 'AI Processing', desc: 'Dual-engine OCR + ML pattern recognition', icon: '🧠' },
    { step: '04', title: 'Get Results', desc: 'Structured JSON with confidence scores', icon: '📊' },
  ];

  const stats = [
    { value: '98%', label: 'OCR Accuracy', icon: '🎯' },
    { value: '1-3s', label: 'Processing', icon: '⚡' },
    { value: '50K+', label: 'Daily Capacity', icon: '📈' },
    { value: '99.9%', label: 'Uptime SLA', icon: '🛡️' },
    { value: '4', label: 'AI Suites', icon: '🏢' },
    { value: '10+', label: 'Bot Clients', icon: '🤖' },
  ];

  const techStack = [
    { name: 'Tesseract OCR', desc: 'Primary engine — 85-95% accuracy', icon: '⚙️' },
    { name: 'EasyOCR', desc: 'Deep Learning CNN+RNN — 90-98%', icon: '🧠' },
    { name: 'OpenCV', desc: 'Image preprocessing & enhancement', icon: '🖼️' },
    { name: 'Flask REST API', desc: 'High-performance Python backend for both OCR & chatbot', icon: '🐍' },
    { name: 'JWT Security', desc: 'Enterprise-grade token auth', icon: '🔒' },
    { name: 'MySQL + PHP', desc: 'Multi-tenant data isolation, chat logs & lead capture', icon: '🗄️' },
    { name: 'Web Speech API', desc: 'Browser-native voice input & TTS output for chatbot', icon: '🎤' },
    { name: 'JS Widget SDK', desc: 'Zero-dependency embeddable chatbot widget with offline fallback', icon: '🔌' },
  ];

  const securityFeatures = [
    'JWT token-based authentication with auto-expiry',
    'Multi-tenant architecture with complete data isolation',
    'Feature-level granular access control per client',
    'API rate limiting & usage monitoring',
    'Encrypted password storage for all accounts',
    'Request validation, sanitization & audit logging',
    'Secure file handling with type & size validation',
    'Role-based admin access (Master / Supervisor / Support)',
  ];

  const useCases = [
    { icon: '🏦', title: 'NBFCs & Banks', desc: 'KYC verification, bank statement analysis, loan eligibility engine' },
    { icon: '👥', title: 'HR & Staffing', desc: 'Resume parsing, trainable ATS scoring, candidate tests with proctoring' },
    { icon: '⚖️', title: 'CA & Law Firms', desc: 'Notice tracking, compliance calendar, AI document summaries' },
    { icon: '💳', title: 'Fintech & KYC', desc: 'Govt ID cross-verification, risk scoring, fraud flag detection' },
    { icon: '🏥', title: 'Insurance', desc: 'Claim document automation, ID verification & extraction' },
    { icon: '🏢', title: 'Enterprise', desc: 'Bulk document processing with multi-tenant data isolation' },
    { icon: '🎓', title: 'EdTech & Coaching', desc: 'AI chatbot for student queries, course info, lead capture & live chat handoff' },
    { icon: '🛍️', title: 'Retail & E-Commerce', desc: 'Embeddable AI support bot with product knowledge, FAQs & offline fallback' },
  ];

  const privacyContent = `
**Last Updated: January 2025**

**1. Information We Collect**
We collect document data solely for processing purposes. Uploaded documents are processed in real-time and are not permanently stored unless explicitly configured by the tenant administrator.

**2. How We Use Your Data**
- Document processing and OCR extraction
- API usage analytics and performance monitoring
- Service improvement and accuracy optimization
- Billing and account management

**3. Data Storage & Security**
- All data is encrypted in transit and at rest
- Multi-tenant architecture ensures complete data isolation
- Documents are processed in isolated environments
- API keys and passwords are encrypted using industry-standard methods

**4. Data Retention**
- Processed results are stored per tenant configuration
- API logs are retained for 90 days for monitoring
- Tenants can request data deletion at any time

**5. Third-Party Sharing**
We do not sell, trade, or share your document data with any third parties. Processing is done entirely on our infrastructure.

**6. Your Rights**
- Access your processed data via client portal
- Request data export in multiple formats
- Request account and data deletion
- Opt out of analytics collection

**7. Contact**
For privacy concerns, contact your tenant administrator or reach us at privacy@grparsing.com
  `;

  const termsContent = `
**Last Updated: January 2025**

**1. Acceptance of Terms**
By accessing or using the Griffso AI Document Processing System, you agree to be bound by these Terms of Service.

**2. Service Description**
Griffso provides AI-powered document processing services including OCR extraction, document classification, resume parsing, and intelligent summarization through REST APIs and client portals.

**3. Account & Access**
- Each tenant receives unique API credentials
- You are responsible for maintaining credential security
- Sharing API keys outside your organization is prohibited
- We reserve the right to suspend accounts violating usage policies

**4. Usage Limits**
- API usage is governed by your subscription plan
- Exceeding plan limits will result in 429 rate-limit responses
- Unlimited plans are subject to fair-use policies
- DDoS or abusive patterns will result in immediate suspension

**5. Acceptable Use**
You agree NOT to:
- Upload malicious files or attempt system exploitation
- Reverse-engineer the AI models or processing pipeline
- Use the service for illegal document forgery or fraud
- Exceed rate limits through automated abuse

**6. Intellectual Property**
- The AI models, algorithms, and system architecture are proprietary
- Processed results belong to the uploading tenant
- API documentation is provided for integration purposes only

**7. Service Availability**
- We target 99.9% uptime SLA
- Scheduled maintenance windows will be communicated in advance
- Force majeure events are excluded from SLA calculations

**8. Limitation of Liability**
- OCR results are provided "as-is" with confidence scores
- We are not liable for decisions made based on extracted data
- Maximum liability is limited to fees paid in the preceding 12 months

**9. Termination**
Either party may terminate with 30 days written notice. Upon termination, tenant data will be retained for 30 days before permanent deletion.

**10. Governing Law**
These terms are governed by the laws of India. Disputes shall be resolved through arbitration in the jurisdiction of the service provider.
  `;

  const renderMarkdown = (text) => {
    return text.trim().split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) return <h4 key={i} className="lp-legal-heading">{line.replace(/\*\*/g, '')}</h4>;
      if (line.trim() === '') return <br key={i} />;
      if (line.startsWith('- ')) return <li key={i}>{line.slice(2)}</li>;
      return <p key={i} className="lp-legal-para">{line.replace(/\*\*/g, '')}</p>;
    });
  };

  return (
    <div className="lp">
      {/* Floating Orbs */}
      <div className="lp-orb lp-orb-1" style={{ transform: `translateY(${scrollY * 0.15}px)` }} />
      <div className="lp-orb lp-orb-2" style={{ transform: `translateY(${scrollY * -0.1}px)` }} />
      <div className="lp-orb lp-orb-3" style={{ transform: `translateY(${scrollY * 0.08}px)` }} />

      {/* Navbar */}
      <nav className={`lp-nav ${scrollY > 50 ? 'scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <div className="lp-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <span className="lp-logo-icon">🚀</span>
            <span className="lp-logo-text">Griffso<span className="lp-logo-ai">AI</span></span>
          </div>
          <div className="lp-nav-links">
            <a href="#features">Features</a>
            <a href="#how">How It Works</a>
            <a href="#demo-cta">Get Started</a>
            <a href="#security">Security</a>
            <a href="#about">About</a>
          </div>
          <div className="lp-nav-actions">
            <button className="lp-btn-ghost" onClick={() => nav('/parsingmodel')}>Client Login</button>
            <button className="lp-btn-primary" onClick={() => nav('/admin')}>Admin Portal</button>
          </div>
          <button className="lp-hamburger" onClick={() => setMobileMenu(true)} aria-label="Open menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`lp-mobile-menu ${mobileMenu ? 'open' : ''}`}>
        <button className="lp-mobile-menu-close" onClick={() => setMobileMenu(false)}>✕</button>
        <a href="#features" onClick={() => setMobileMenu(false)}>Features</a>
        <a href="#how" onClick={() => setMobileMenu(false)}>How It Works</a>
        <a href="#demo-cta" onClick={() => setMobileMenu(false)}>Get Started</a>
        <a href="#security" onClick={() => setMobileMenu(false)}>Security</a>
        <a href="#about" onClick={() => setMobileMenu(false)}>About</a>
        <div className="lp-mobile-menu-actions">
          <button className="lp-btn-ghost" onClick={() => { nav('/parsingmodel'); setMobileMenu(false); }}>Client Login</button>
          <button className="lp-btn-primary" onClick={() => { nav('/demo'); setMobileMenu(false); }}>🧪 Try Live Demo</button>
        </div>
      </div>

      {/* Hero */}
      <section className="lp-hero">
        <div className="lp-hero-content">
          <div className="lp-hero-badge">🏆 Enterprise-Grade AI Document Processing</div>
          <h1 className="lp-hero-title">
            Transform Documents Into<br />
            <span className="lp-gradient-text">Structured Intelligence</span>
          </h1>
          <p className="lp-hero-sub">
            Three industry-specific suites — HR, Legal & CA, and NBFC/Financial — built on dual-engine OCR
            with ML pattern recognition. ATS scoring, proctoring, notice tracking, KYC, loan eligibility & more.
          </p>
          <div className="lp-hero-actions">
            <button className="lp-btn-hero" onClick={() => nav('/parsingmodel')}>Start Processing →</button>
            <button className="lp-btn-hero-outline" onClick={() => nav('/demo')}>Try Live Demo</button>
          </div>
          <div className="lp-hero-stats">
            {stats.slice(0, 4).map((s, i) => (
              <div key={i} className="lp-hero-stat">
                <span className="lp-hero-stat-icon">{s.icon}</span>
                <span className="lp-hero-stat-val">{s.value}</span>
                <span className="lp-hero-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-hero-visual">
          <div className="lp-hero-card">
            <div className="lp-hero-card-header">
              <span>📄 Document Processing</span>
              <span className="lp-pulse-dot" />
            </div>
            <div className="lp-hero-card-body">
              <div className="lp-code-line"><span className="lp-code-key">POST</span> /process</div>
              <div className="lp-code-line"><span className="lp-code-str">document_type:</span> "PAN"</div>
              <div className="lp-code-line"><span className="lp-code-str">confidence:</span> <span className="lp-code-num">98.5%</span></div>
              <div className="lp-code-line"><span className="lp-code-str">pan_number:</span> "ABCDE1234F"</div>
              <div className="lp-code-line"><span className="lp-code-str">processing_time:</span> <span className="lp-code-num">1.2s</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="lp-stats-bar">
        {stats.map((s, i) => (
          <div key={i} className="lp-stat-item">
            <span className="lp-stat-icon">{s.icon}</span>
            <span className="lp-stat-val">{s.value}</span>
            <span className="lp-stat-label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* Verticals */}
      <section id="features" className="lp-section">
        <div className="lp-section-header">
          <span className="lp-section-badge">✨ Industry Suites</span>
          <h2>Three Verticals. One Platform.</h2>
          <p>Feature access is unlocked per client — only the tabs relevant to their enabled features are visible</p>
        </div>
        <div className="lp-verticals">
          {verticals.map((v, vi) => (
            <div key={vi} className="lp-vertical-card" style={{ '--vcolor': v.color }}>
              <div className="lp-vertical-header">
                <span className="lp-vertical-badge">{v.badge}</span>
                <span className="lp-vertical-trigger">Trigger: {v.trigger}</span>
              </div>
              <div className="lp-vertical-features">
                {v.features.map((f, fi) => (
                  <div key={fi} className="lp-vfeature">
                    <span className="lp-vfeature-icon">{f.icon}</span>
                    <div>
                      <h4>{f.title}</h4>
                      <p>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OCR Doc Types */}
      <section className="lp-section lp-section-dark">
        <div className="lp-section-header">
          <span className="lp-section-badge">📄 OCR Engine</span>
          <h2>Supported Document Types</h2>
          <p>8 specialized AI models for Indian government IDs, resumes, legal notices & financial documents</p>
        </div>
        <div className="lp-features-grid">
          {features.map((f, i) => (
            <div key={i} className="lp-feature-card" style={{ '--accent': f.color }}>
              <div className="lp-feature-glow" />
              <span className="lp-feature-icon">{f.icon}</span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="lp-section">
        <div className="lp-section-header">
          <span className="lp-section-badge">🔄 Workflow</span>
          <h2>How It Works</h2>
          <p>From upload to structured data in 4 simple steps</p>
        </div>
        <div className="lp-steps">
          {howItWorks.map((s, i) => (
            <div key={i} className="lp-step">
              <div className="lp-step-num">{s.step}</div>
              <span className="lp-step-icon">{s.icon}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              {i < 3 && <div className="lp-step-arrow">→</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="lp-section lp-section-dark">
        <div className="lp-section-header">
          <span className="lp-section-badge">🏢 Industries</span>
          <h2>Built For Enterprise</h2>
          <p>Trusted across industries for mission-critical document processing</p>
        </div>
        <div className="lp-usecases-grid">
          {useCases.map((u, i) => (
            <div key={i} className="lp-usecase-card">
              <span className="lp-usecase-icon">{u.icon}</span>
              <h3>{u.title}</h3>
              <p>{u.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AI Chatbot Section */}
      <section className="lp-section lp-chatbot-section">
        <div className="lp-section-header">
          <span className="lp-section-badge">🤖 AI Chatbot Suite</span>
          <h2>Conversational AI — Embedded Anywhere</h2>
          <p>White-label AI chatbot with voice I/O, domain knowledge base, live chat handoff & offline fallback. One script tag — works on any website.</p>
        </div>

        {/* Demo preview card */}
        <div className="lp-chatbot-showcase">
          <div className="lp-chatbot-preview">
            <div className="lp-chatbot-window">
              <div className="lp-chatbot-header">
                <div className="lp-chatbot-avatar">🤖</div>
                <div>
                  <div className="lp-chatbot-name">Griffso AI Bot</div>
                  <div className="lp-chatbot-status"><span className="lp-pulse-dot" /> Online</div>
                </div>
              </div>
              <div className="lp-chatbot-messages">
                <div className="lp-chat-msg lp-chat-bot">
                  <span>👋 Welcome! I'm your AI assistant. How can I help you today?</span>
                </div>
                <div className="lp-chat-msg lp-chat-user">
                  <span>What courses do you offer?</span>
                </div>
                <div className="lp-chat-msg lp-chat-bot">
                  <span>We offer Foundation, Prelims, Mains & Interview programs. Which one interests you? 🎯</span>
                </div>
                <div className="lp-chat-typing">
                  <span /><span /><span />
                </div>
              </div>
              <div className="lp-chatbot-input">
                <span>🎤</span>
                <div className="lp-chatbot-input-bar">Type a message...</div>
                <span>➤</span>
              </div>
            </div>
            <div className="lp-chatbot-float-btn">💬</div>
          </div>

          <div className="lp-chatbot-info">
            <div className="lp-chatbot-clients">
              <div className="lp-chatbot-clients-label">🏆 Live on 10+ platforms</div>
              <div className="lp-chatbot-client-logos">
                {['Rogi Sewa'].map((c, i) => (
                  <span key={i} className="lp-chatbot-client-chip">{c}</span>
                ))}
              </div>
            </div>
            <div className="lp-chatbot-features-list">
              {chatbotFeatures.map((f, i) => (
                <div key={i} className="lp-chatbot-feat" style={{ '--cbcolor': f.color }}>
                  <span className="lp-chatbot-feat-icon">{f.icon}</span>
                  <div>
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Embed snippet */}
        <div className="lp-chatbot-embed">
          <div className="lp-chatbot-embed-label">🔌 Embed in 1 line</div>
          <div className="lp-chatbot-embed-code">
            <code>{`<script src="https://ai.griffso.com/chat_bot/chatbot-widget.js" data-api-key="YOUR_KEY"></script>`}</code>
          </div>
        </div>

        {/* Know More CTA */}
        <div className="lp-chatbot-cta">
          <button className="lp-chatbot-know-more" onClick={() => setChatbotModal(true)}>
            💡 How Profitable Is a Chatbot for Your Business?
          </button>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="lp-section">
        <div className="lp-section-header">
          <span className="lp-section-badge">🛠️ Technology</span>
          <h2>Enterprise Technology Stack</h2>
          <p>Built on proven, battle-tested technologies</p>
        </div>
        <div className="lp-tech-grid">
          {techStack.map((t, i) => (
            <div key={i} className="lp-tech-card">
              <span className="lp-tech-icon">{t.icon}</span>
              <h4>{t.name}</h4>
              <p>{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Security */}
      <section id="security" className="lp-section">
        <div className="lp-section-header">
          <span className="lp-section-badge">🔒 Security</span>
          <h2>Enterprise-Grade Security</h2>
          <p>Your documents are protected by multiple layers of security</p>
        </div>
        <div className="lp-security-grid">
          {securityFeatures.map((s, i) => (
            <div key={i} className="lp-security-item">
              <span className="lp-check">✅</span>
              <span>{s}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Schedule Demo / CTA */}
      <section id="demo-cta" className="lp-section lp-section-dark">
        <div className="lp-cta-box">
          <div className="lp-cta-content">
            <span className="lp-section-badge">🎯 Get Started</span>
            <h2>Ready to Transform Your Document Processing?</h2>
            <p>Schedule a quick demo with our team or start your free trial today. No credit card required.</p>
            <div className="lp-cta-features">
              <span>✅ Free trial available</span>
              <span>✅ Custom plans for enterprise</span>
              <span>✅ Dedicated onboarding support</span>
              <span>✅ Volume discounts</span>
            </div>
            <div className="lp-cta-actions">
              <button className="lp-btn-hero" onClick={() => window.location.href='mailto:griffso574@gmail.com?subject=Schedule%20a%20Demo'}>📅 Schedule a Demo</button>
              <button className="lp-btn-hero-outline" onClick={() => nav('/parsingmodel')}>Start Free Trial →</button>
              <button className="lp-btn-hero-outline" onClick={() => nav('/demo')}>🧪 Try Live Demo</button>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="lp-section">
        <div className="lp-section-header">
          <span className="lp-section-badge">🚀 About</span>
          <h2>About Griffso AI</h2>
        </div>
        <div className="lp-about-content">
          <div className="lp-about-text">
            <p>
              Griffso AI is an enterprise-grade document intelligence platform built around four
              AI products — HR Suite, Legal & CA Suite, NBFC/Financial Suite, and an embeddable
              AI Chatbot. Each client sees only the features relevant to their business, unlocked
              by the document types and modules they subscribe to.
            </p>
            <p>
              The HR Suite brings trainable ATS scoring, candidate test generation with proctoring,
              and a full pipeline manager. The Legal & CA Suite tracks notices, builds a compliance
              calendar, and maintains a searchable AI summary library. The Financial Suite delivers
              deep bank statement analysis, KYC cross-verification, and a configurable loan eligibility
              engine.
            </p>
            <p>
              The AI Chatbot Suite is a white-label conversational AI platform — deployed live on
              10+ EdTech platforms.
              It features voice I/O, a 150+ topic domain knowledge base, live chat handoff, and an
              offline fallback mode that keeps the bot answering even when the server is down.
            </p>
            <p>
              All products run on a multi-tenant architecture with JWT security, granular feature
              access control, and complete data isolation — serving businesses from startups to
              enterprises processing 50,000+ documents and 10,000+ chat queries per day.
            </p>
          </div>
          <div className="lp-about-highlights">
            {[
              { icon: '🎯', label: '98% Accuracy on Government IDs' },
              { icon: '⚡', label: '1-3 Second Processing Time' },
              { icon: '👥', label: 'HR Suite — ATS, Tests, Pipeline' },
              { icon: '⚖️', label: 'Legal Suite — Notices, Calendar, Summaries' },
              { icon: '🏦', label: 'Financial Suite — KYC, Loans, Risk' },
              { icon: '🤖', label: 'AI Chatbot — Voice, Widget, 10+ Live Clients' },
              { icon: '🔌', label: 'Embeddable — 1-line JS widget on any site' },
              { icon: '🔒', label: 'Multi-Tenant with Feature-Level Access Control' },
            ].map((h, i) => (
              <div key={i} className="lp-about-highlight">
                <span>{h.icon}</span>
                <span>{h.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Links */}
      <section className="lp-section lp-section-dark lp-legal-section">
        <div className="lp-legal-links">
          <button onClick={() => setModal('privacy')}>📜 Privacy Policy</button>
          <button onClick={() => setModal('terms')}>📋 Terms & Conditions</button>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <span className="lp-logo-icon">🚀</span>
            <span className="lp-logo-text">Griffso<span className="lp-logo-ai">AI</span></span>
            <p>Enterprise AI Document Processing Platform</p>
          </div>
          <div className="lp-footer-links">
            <div>
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#demo-cta">Get Started</a>
              <a href="#how">How It Works</a>
            </div>
            <div>
              <h4>Company</h4>
              <a href="#about">About</a>
              <a href="#security">Security</a>
              <button onClick={() => setModal('privacy')}>Privacy Policy</button>
              <button onClick={() => setModal('terms')}>Terms</button>
            </div>
            <div>
              <h4>Access</h4>
              <button onClick={() => nav('/parsingmodel')}>Client Portal</button>
              <button onClick={() => nav('/admin')}>Admin Dashboard</button>
              <button onClick={() => nav('/demo')}>Live Demo</button>
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <p>© {new Date().getFullYear()} Griffso AI. All rights reserved. Built with ❤️ for enterprise document intelligence.</p>
        </div>
      </footer>

      {/* Chatbot ROI Modal */}
      {chatbotModal && (
        <div className="lp-modal-overlay" onClick={() => setChatbotModal(false)}>
          <div className="lp-modal lp-chatbot-roi-modal" onClick={e => e.stopPropagation()}>
            <div className="lp-modal-header">
              <h3>💡 Why AI Chatbot = Real Business Profit</h3>
              <button onClick={() => setChatbotModal(false)}>✕</button>
            </div>
            <div className="lp-modal-body">
              <p className="lp-roi-intro">Companies using AI chatbots report <strong>60–80% reduction in support costs</strong> and <strong>3x higher lead conversion</strong>. Here’s exactly how it pays for itself:</p>
              <div className="lp-roi-grid">
                {chatbotROI.map((r, i) => (
                  <div key={i} className="lp-roi-card">
                    <span className="lp-roi-icon">{r.icon}</span>
                    <div>
                      <h4>{r.title}</h4>
                      <p>{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="lp-roi-footer">
                <p>🚀 Already live on <strong>10+ platforms</strong> </p>
                <button className="lp-btn-hero" onClick={() => { setChatbotModal(false); window.location.href='mailto:griffso574@gmail.com?subject=Chatbot%20Demo'; }}>
                  📅 Get a Free Chatbot Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legal Modal */}
      {modal && (
        <div className="lp-modal-overlay" onClick={() => setModal(null)}>
          <div className="lp-modal" onClick={e => e.stopPropagation()}>
            <div className="lp-modal-header">
              <h3>{modal === 'privacy' ? '📜 Privacy Policy' : '📋 Terms & Conditions'}</h3>
              <button onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="lp-modal-body">
              {renderMarkdown(modal === 'privacy' ? privacyContent : termsContent)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
